import { Box, Divider, Grid, IconButton, Link, List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, Paper, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { AddCircleOutline, AssignmentOutlined, BusinessOutlined, CalendarMonthOutlined, Check, Close, ComputerOutlined, DesignServicesOutlined, HelpOutlineOutlined, InsertDriveFileOutlined, KeyboardArrowLeft, KeyboardArrowRight, Phone, RateReviewOutlined, ScheduleOutlined, SupportAgentOutlined } from "@mui/icons-material";
import React, { useEffect, useMemo, useState } from "react";
import { NOTIFICATION_TYPE, showNotificationBar } from "../../../actions/error-pages/NotificationBarActions";
import { getSiteContent, getSiteHoursReport, getSiteSubmissions, getSiteSubscription } from '../../../actions/internal/SiteActions';
import { getSiteById, getSiteContentById, getSiteSubmissionsById, getSiteSubscriptionById } from '../../../selectors/SiteSelectors';
import Colors from "../../../constants/Colors";
import { formatDateTimeToFancyString, formatDateToFancyString, getMonthName } from "../../../utilities/DateUtilities";
import { withRouter } from 'react-router';
import { observer } from 'mobx-react-lite';
import StyledSubHeader from '../../common/StyledSubHeader';
import StyledProgressCircle from '../../common/StyledProgressCircle';
import { ContainedButton, TextButton, TooltipIconButton } from '../../common/StyledButtons';
import { CARD_CAPTION_STYLE, CARD_HEADER_STYLE } from '../../../constants/Styles';
import { getUserId } from '../../../selectors/UserSelectors';
import StyledChip from '../../common/StyledChip';
import StyledPage from '../../common/StyledPage';
import StyledSection from '../../common/StyledSection';
import StyledStatusIcon from '../../common/StyledStatusIcon';
import { GENERATION_STATUS, SUBMISSION_TYPES_HUMAN_MAP } from '../../../constants/Statuses';
import ViewRequestModal from '../modals/ViewRequestModal';
import AddRequestModal from '../modals/AddRequestModal';
import { isAdmin } from '../../../actions/profile/UserActions';

const ViewSiteContentPage = ({ match, history }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [ isLoading, setIsLoading ] = useState(true);

    const siteId = match.params.site_id
    const content = getSiteContentById(siteId);

    useEffect(() => {
        loadData().then();
    }, [])

    const loadData = async () => {
        try {
            const userId = getUserId();
            await getSiteContent(userId, siteId)
        } catch (ex) {
            console.error(ex);
        } finally {
            setIsLoading(false);
        }
    }

    const sortedContent = useMemo(() => {
        if (!content || !Array.isArray(content)) {
            return [];
        }
    
        const unpublishedStatuses = content.filter((c) =>
            c.generator_status !== 'CREATION_SUCCESS'
            && c.generator_status !== 'CREATION_PUBLISHED'
        );
    
        const publishedOrSuccessful = content.filter((c) =>
            c.generator_status === 'CREATION_SUCCESS'
            || c.generator_status === 'CREATION_PUBLISHED'
        );
    
        // 1. Unpublished sorted by updated_timestamp
        unpublishedStatuses.sort((a, b) => {
            const aUpdated =
                a && a.metadata && a.metadata.updated_timestamp
                    ? a.metadata.updated_timestamp
                    : 0;
    
            const bUpdated =
                b && b.metadata && b.metadata.updated_timestamp
                    ? b.metadata.updated_timestamp
                    : 0;
    
            return bUpdated - aUpdated;
        });
    
        // 2. Success first, then Published
        // 3. Within each group, sort by scheduled_date → fallback
        publishedOrSuccessful.sort((a, b) => {
            const getSortTime = (item: any) => {
                const scheduled =
                    item &&
                    item.publishing &&
                    item.publishing.scheduled_date
                        ? new Date(item.publishing.scheduled_date).getTime()
                        : 0;
    
                if (scheduled > 0) return scheduled;
    
                return item &&
                    item.metadata &&
                    item.metadata.generation_start_timestamp
                    ? item.metadata.generation_start_timestamp
                    : 0;
            };
    
            const aStatus = a && a.generator_status;
            const bStatus = b && b.generator_status;
    
            // Status priority: SUCCESS before PUBLISHED
            if (aStatus !== bStatus) {
                if (aStatus === 'CREATION_SUCCESS') return -1;
                if (bStatus === 'CREATION_SUCCESS') return 1;
            }
    
            // Same status → sort by date
            const aTime = getSortTime(a);
            const bTime = getSortTime(b);
    
            return bTime - aTime;
        });
    
        return unpublishedStatuses.concat(publishedOrSuccessful);
    }, [content]);

    const renderLoadingContent = () => (
        <StyledProgressCircle />
    )

    const renderArticleName = (article) => {
        if(!article) return 'N/A';
        const title = article.content.topic ? article.content.topic : 'Untitled Article';
        return <span>{title}</span>
    }

    const renderRequestsTooltip = () => (
        <Tooltip
            title={
            <>
                This section shows all active requests open for your business.
                Requests are a way to submit necessary changes or ask for help with problems you might be facing.
            </>
            }
            arrow
            placement="top"
        >
            <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>Requests</span>
            <HelpOutlineOutlined
                fontSize="small"
                sx={{ marginLeft: '4px', cursor: 'pointer' }}
            />
            </div>
        </Tooltip>
    )

    const renderLibraryTooltip = () => (
        <Tooltip
            title={
            <>
                This section includes all content currently available to you. From here, you can easily edit, publish, or hide items based on your needs.
            </>
            }
            arrow
            placement="top"
        >
            <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>My Content</span>
            <HelpOutlineOutlined
                fontSize="small"
                sx={{ marginLeft: '4px', cursor: 'pointer' }}
            />
            </div>
        </Tooltip>
    )

    const renderOpenNewButton = () => (
        <TextButton
          onClick={() => history.push(`/dashboard/site/${siteId}/content/create`)}
          sx={{ display: 'flex', alignItems: 'center', padding: '4px' }}
        >
          <AddCircleOutline fontSize="small" sx={{ marginRight: '4px' }} />
          Create New Content
        </TextButton>
    );

    const renderBetaBanner = () => (
        <Box
          sx={{
            border: '2px solid #10AA00',
            backgroundColor: '#E9FBE7', // light green tint for readability
            color: '#000',
            padding: isMobile ? '16px' : '20px 24px',
            borderRadius: '4px',
            marginBottom: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}
        >
          <h3 style={{ margin: 0, fontSize: isMobile ? '18px' : '20px' }}>
            Welcome to BCK Content Creation (Beta)
          </h3>
      
          <p style={{ margin: 0, fontSize: '14px', lineHeight: '16px' }}>
            You’re invited to explore and try out our content creation tools. This is an early version, so things may not be perfect just yet.
          </p>
      
          <p style={{ margin: 0, fontSize: '14px', lineHeight: '16px' }}>
            We’d love your feedback. Your input helps us improve and shape the experience moving forward.
          </p>
        </Box>
      );

    const buildArticleIcon = (content) => {
        if (!content || !content.metadata || !content.metadata.published_timestamp) {
          return <StyledStatusIcon status={content.generator_status} />;
        }
        const timestamp = content.metadata.published_timestamp;
      
        const publishDate = new Date(timestamp);
        const now = new Date();
      
        const isFuture = publishDate.getTime() > now.getTime();
      
        return isFuture ? <Tooltip title={"Scheduled: This article will be published at a future time."}><CalendarMonthOutlined style={{ color: Colors.orangeColor,cursor: 'pointer', fontSize: '28px' }}/></Tooltip> : <StyledStatusIcon status={content.generator_status} />;
      }

      const getLibraryItemSecondaryText = (c: any) => {
        const now = Date.now();
    
        const status = c && c.generator_status;
        const metadata = c && c.metadata;
        const publishing = c && c.publishing;
    
        if (status === GENERATION_STATUS.PUBLISHED) {
            let scheduledPublishTime = null;
    
            if (
                publishing &&
                publishing.scheduled &&
                publishing.scheduled_date
            ) {
                scheduledPublishTime = new Date(publishing.scheduled_date).getTime();
            }
    
            if (scheduledPublishTime && scheduledPublishTime > now) {
                return `Scheduled for ${formatDateToFancyString(scheduledPublishTime)}`;
            }
    
            if (metadata && metadata.published_timestamp) {
                return `Published on ${formatDateToFancyString(metadata.published_timestamp)}`;
            }
    
            return 'Published';
        }
    
        if (status === GENERATION_STATUS.SUCCESS) {
            if (metadata && metadata.generation_start_timestamp) {
                return `Written on ${formatDateToFancyString(metadata.generation_start_timestamp)}`;
            }
    
            return 'Written';
        }
    
        if (status === GENERATION_STATUS.PENDING) {
            return 'Writing currently in progress...';
        }
    
        if (metadata && metadata.created_timestamp) {
            return `Submitted on ${formatDateToFancyString(metadata.created_timestamp)}`;
        }
    
        return 'Submitted';
    };

      const renderLibrarySection = () => (
        <StyledSection
            title={<>{renderLibraryTooltip()}</>}
            rightAdornment={isAdmin() && renderOpenNewButton()}
        >
            {isLoading ? 
                <StyledProgressCircle />
                :
                <Paper variant='outlined' sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px' }}>
                    <List style={{ flex: 1 }}>
                        {sortedContent && sortedContent.map((c, index) => (
                            <ListItem
                            dense
                            key={c.content_id}
                            sx={{
                                backgroundColor: '#fff',
                                marginBottom: '5px',
                                paddingRight: '55px',
                                borderBottom: index !== content.length - 1 ? '1px solid #e0e0e0' : 'none',
                            }}
                            >
                            <ListItemAvatar>
                                {buildArticleIcon(c)}
                            </ListItemAvatar>

                            <ListItemText
                                primary={renderArticleName(c)}
                                secondary={getLibraryItemSecondaryText(c)}
                            />
                            {(c.generator_status === GENERATION_STATUS.SUCCESS
                                || c.generator_status === GENERATION_STATUS.PUBLISHED) &&
                                <ListItemSecondaryAction sx={{ marginLeft: "10px" }}>
                                    <TooltipIconButton
                                    text="View Article"
                                    icon={
                                        <InsertDriveFileOutlined
                                            sx={{ color: Colors.primaryColor }}
                                            onClick={() => history.push(`/dashboard/site/${siteId}/content/${c.tracking_id}`)}
                                        />
                                    }
                                    />
                                </ListItemSecondaryAction>
                            }
                            
                            </ListItem>
                        ))}
                        
                        {(!sortedContent || sortedContent.length === 0) && <p>No content created yet.</p>}
                    </List>
                </Paper>
            }
        </StyledSection>
    )

    /* const renderRequestsSection = () => (
        <StyledSection
            title={<>{renderRequestsTooltip()}</>}
            rightAdornment={renderOpenNewButton()}
        >
            {isLoading ? 
                <StyledProgressCircle />
                :
                <Paper variant='outlined' sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px' }}>
                    <List style={{ flex: 1 }}>
                        {content && content.map((c, index) => (
                            <ListItem
                            dense
                            key={c.content_id}
                            sx={{
                                backgroundColor: '#fff',
                                marginBottom: '5px',
                                paddingRight: '55px',
                                borderBottom: index !== content.length - 1 ? '1px solid #e0e0e0' : 'none',
                            }}
                            >
                            <ListItemAvatar>
                                {buildArticleIcon(c)}
                            </ListItemAvatar>

                            <ListItemText
                                primary={renderArticleName(c)}
                                secondary={`Written on ${formatDateToFancyString(c.metadata.generation_start_timestamp)}`}
                            />
                            {(c.generator_status === GENERATION_STATUS.SUCCESS
                                || c.generator_status === GENERATION_STATUS.PUBLISHED) &&
                                <ListItemSecondaryAction sx={{ marginLeft: "10px" }}>
                                    <TooltipIconButton
                                    text="View Article"
                                    icon={
                                        <InsertDriveFileOutlined
                                            sx={{ color: Colors.primaryColor }}
                                            onClick={() => history.push(`/dashboard/site/${siteId}/content/${c.tracking_id}`)}
                                        />
                                    }
                                    />
                                </ListItemSecondaryAction>
                            }
                            
                            </ListItem>
                        ))}
                        
                        {(!content || content.length === 0) && <p>No content created yet.</p>}
                    </List>
                </Paper>
            }
        </StyledSection>
    ) */
    

    return (
        <>
            <StyledPage>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <StyledSubHeader title="Content" />
                    </Grid>

                    <Grid item xs={12}>
                    {isLoading ? (
                        renderLoadingContent()
                    ) : (
                        <>
                            {renderBetaBanner()}
                            {renderLibrarySection()}
                        </>
                    )}
                    </Grid>
                </Grid>
            </StyledPage>
      </>
    )


}

export default observer(withRouter(ViewSiteContentPage));
